<template>
  <div class="chart-w importance-chart d-block w-100 relative">
    <div class="importance-chart__wrapper">
      <Apexchart
        v-if="!loading"
        :key="`${refreshConter}imp`"
        :options="chartOptions"
        :series="(newSeries.length ? newSeries : series) || []"
        :height="stylesChart.heightStackedHorizontalBarChart"
      />
    </div>

    <div class="importance-chart__menu">
      <div class="importance-chart__menu-left">
        <div class="importance-chart__title">
          {{ t('importance_stat_bar.title') }}
        </div>
      </div>
      <div class="importance-chart__menu-right">
        <RefreshIcon @click="loadData" />
        <CalendarInput v-model="date" />
        <RubricList
          v-model:selected-chart="selectedChart"
          :active-store="activeStore"
          :date="date"
          all-enabled
          @clearRubrics="clearRubrics"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, toRaw, ref, reactive, watch } from "vue";
import VueApexCharts from "vue3-apexcharts";
import { useImportanceStatBlocksStore } from "@/store/Analytic/chart/importance-stat-dynamic-blocks";
import { useImportanceByRubricStatBlocksStore } from "@/store/Analytic/chart/importance-by-rubric-stat-dynamic-blocks";
import { stylesChart } from "@/tools/tooltipChart";
import { t } from "@/controllerLK/GlobusLocalization";
import useFilterDate from "@/mixins/apexChart/filterDate";
import RubricList from "@/components/AnalyticsPage/AnalyticsGeneral/Charts/blocks/RubricList.vue";
import useColumnChart from "@/mixins/apexChart/columnChart";
import CalendarInput from "@/components/AnalyticsPage/AnalyticsGeneral/Charts/blocks/CalendarInput.vue";
import RefreshIcon from "@/components/Icons/RefreshIcon.vue";
import {importanceList} from "@/const/importance.list";

export default {
  name: "ImportanceStatChart",
  components: {CalendarInput, Apexchart: VueApexCharts, RubricList, RefreshIcon },
  setup() {
    const statStorage = useImportanceStatBlocksStore();
    const statRubricStorage = useImportanceByRubricStatBlocksStore();

    const { date } = useFilterDate(loadData);
    const loading = ref(true);
    const selectedChart = ref(0);
    const newSeries = ref([]);
    const refreshConter = ref(0);

    async function loadData() {
      await statStorage.loadStat(date.value[0], date.value[1], [], true);
      await statRubricStorage.loadStat(date.value[0], date.value[1], [], true);
      loading.value = false;
    }

    const activeStore = computed(() => {
      return selectedChart.value === 1 ? statRubricStorage : statStorage;
    });

    const categoriesForAPI = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const categoriesForUsers = [
      t("globus_calendar.day_short.monday"),
      t("globus_calendar.day_short.tuesday"),
      t("globus_calendar.day_short.wednesday"),
      t("globus_calendar.day_short.thursday"),
      t("globus_calendar.day_short.friday"),
      t("globus_calendar.day_short.saturday"),
      t("globus_calendar.day_short.sunday")
    ];

    const importances = [t("importance_stat_bar.importance.critical"), t("importance_stat_bar.importance.important"), t("importance_stat_bar.importance.moderately"), t("importance_stat_bar.importance.not_critical")];

    function clearRubrics() {
      loading.value = true;
      statStorage.items = [];
      statRubricStorage.items = [];
      newSeries.value = [];
      chartOptions.value = useColumnChart(
        {
          noDataText: t("common_chart.no_data.short_text"),
          titleText: t("importance_stat_bar.title"),
          stacked: false,
        },
        categoriesForUsers,
        [],
      ).chartOptions;
      refreshConter.value++;
      setTimeout(() => {
        loading.value = false;
      }, 0);
    }

    function aggregateByDay(data) {
      loading.value = true;
      const resultObj = {};
      for (const day of categoriesForAPI) {
        resultObj[day] = {};
      }

      for (const entry of data) {
        const { day, countByImportance } = entry;
        if (!resultObj[day]) resultObj[day] = {};

        for (const key in countByImportance) {
          if (!resultObj[day][key]) {
            resultObj[day][key] = 0;
          }
          resultObj[day][key] += countByImportance[key];
        }
      }

      // Преобразуем в массив объектов
      const resByDays = categoriesForAPI.map(day => ({
        name: day,
        data: resultObj[day],
      }));

      // Все возможные ключи, в нужном порядке
      const keys = ["4", "3", "2", "1"];

      // // Инициализируем результат
      // const result = keys.map((key, index) => ({
      //   name: [importances[index]],
      //   color: getImportanceButtonColor(Number(key)),
      //   data: []
      // }));

      const result = Object.values(importanceList).filter((importance) => {
        return importance.importance !== null && importance.importance !== 0;
      }).toReversed().map((item) => {
        return {
          name: t(`importance_stat_bar.importance.${item.name}`),
          color: item.analyticColor,
          data: []
        };
      });

      console.log(result);


      keys.forEach((key, idx) => {
        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
          const dayData = resByDays[dayIndex].data;
          const value = key in dayData ? dayData[key] : 0;
          result[idx].data.push(value);
        }
        if (result[idx].data.every((item) => item === 0)) {
          result[idx].data = []; // Если приходят пустые объекты, то не отображаем пустой график, а отображаем "По вашему запросу ничего не найдено"
        }
      });
      loading.value = false;
      return result;
    }


    const beforeUpdate = () => {
      loading.value = true;
      if (newSeries?.value?.length) {
        newSeries.value = [];
      }
    };

    watch(
      () => selectedChart.value,
      () => {
        loading.value = true;
        setTimeout(() => {
          chartOptions.value = useColumnChart(
            {
              titleText: t("importance_stat_bar.title"),
              stacked: false,
            },
            categoriesForUsers || [],
            series.value || [],
          ).chartOptions;
          setTimeout(() => loading.value = false, 0);
        }, 0);
      },
    );

    const series = computed(() => aggregateByDay(activeStore?.value?.items || []));

    const chartOptions = reactive(useColumnChart(
      {
        titleText: t("importance_stat_bar.title"),
        stacked: false,
      },
      categoriesForUsers,
      series.value || [],
    ).chartOptions);

    return { beforeUpdate, categoriesForUsers, chartOptions, clearRubrics, date, loading, newSeries, series, stylesChart, selectedChart, activeStore, refreshConter, toRaw, t, aggregateByDay, loadData };
  },
};
</script>

<style lang="scss" scoped>

.importance-chart {
  &__title {
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  }

  &__wrapper {
    z-index: 10;
  }

  &__menu {
    display: flex;
    position: absolute;
    align-items: center;
    top: 24px;
    height: 42px;
    width: 100%;
    left: 0;
    padding: 0 24px;
    justify-content: space-between;

    &-left {
      display: flex;
      gap: 20px;
    }

    &-right {
      display: flex;
      gap: 14px;
    }
  }

  &__legends {
    display: flex;
    justify-content: center;
    margin: auto;
    svg {
      margin: 0 4px;
      width: 20px;
      height: 20px;
    }
  }
}

.apexcharts-menu-open {
  margin-top: 15px;
}
</style>
